import { TextFieldSize } from '@bechtel/gds';
import '@bechtel/gds/dist/index.css';
import { ICellRendererParams } from 'ag-grid-community';
import { useMemo, useRef, useState } from 'react';
import { DateFieldComponent } from '~/components';
import { useDate } from '~/hooks';
import { isFieldEditable } from '../../utils';
import { GridCellContainer } from './styled';

interface CustomDateTimeRendererParams extends ICellRendererParams {
  onRowEdited?: (updatedRowData: any) => void; // This includes the onRowEdited function
  rowHeight: string;
}

export const DatetimeCellRenderer = (props: CustomDateTimeRendererParams) => {
  const { onRowEdited, value, column, rowHeight, setValue, colDef, data } = props;
  const { formatLocaleDate, formatLocaleTime, parseDateWithoutTime, formatToISODateRepresentation } = useDate();
  const [hover, setHover] = useState(false);
  const [date, setDate] = useState<string>(colDef?.cellDataType === 'date' ? value?.toString() : value);
  const [clicked, setClicked] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const editable = isFieldEditable(colDef?.editable, props);

  const selectItem = (date: string) => {
    setDate(date);
    const selectedColumn = column?.getColId();
    if (selectedColumn && onRowEdited) {
      onRowEdited({ ...data, [selectedColumn]: date, dateChanged: true });
    }
    if (setValue) {
      setValue(date);
    }
    setClicked(false);
    setHover(false);
  };

  const calculatePopperPlacement = () => {
    if (!datePickerRef.current) return 'bottom'; // Default to bottom if ref is not available

    const cellRect = datePickerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // If the component is near the top of the viewport, open the date-picker below it
    if (cellRect.top < viewportHeight / 2) {
      return 'bottom';
    } else {
      // Otherwise, open the date-picker above it
      return 'top';
    }
  };

  const displayDate = useMemo(() => {
    if (date && new Date(date).toString() !== 'Invalid Date') {
      const formattedDate =
        colDef?.cellDataType === 'date' ? `${formatLocaleDate(date)} ${formatLocaleTime(date)}` : date;
      return formattedDate;
    }
  }, [colDef?.cellDataType, date, formatLocaleDate, formatLocaleTime]);

  return (
    <GridCellContainer
      rowHeight={rowHeight}
      ref={datePickerRef}
      onMouseOver={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onClick={() => {
        setClicked(true);
      }}
      showIcon={hover || clicked}
      editable={editable}
    >
      {!editable && displayDate}
      {editable && (
        <DateFieldComponent
          onChange={(date, event) => {
            selectItem(formatToISODateRepresentation(date));
            event.stopPropagation();
          }}
          showPopperArrow={false}
          size={TextFieldSize.SMALL}
          icon="calendar"
          iconStyle="regular"
          selected={displayDate ? parseDateWithoutTime(displayDate) : null}
          onClickOutside={(event) => {
            setClicked(false);
            setHover(false);
            event.stopPropagation();
          }}
          popperPlacement={calculatePopperPlacement()}
        />
      )}
    </GridCellContainer>
  );
};
